import {
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Slider,
  Switch,
} from '@material-ui/core'
import { AddCircleOutline, ArrowDownward, ArrowUpward, DeleteOutline } from '@material-ui/icons'
import { MultipleSelect } from 'components/controls/form/MultipleSelect'
import { Button, CustomTooltip } from 'components/facets'
import { usePriceGuide } from 'components/providers/PriceGuideProvider'
import { useUser } from 'components/providers/UserProvider'
import { usePersistentStock } from 'lib/hooks/usePersistentStock'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Flag from 'react-world-flags'
import {
  AnyYesNo,
  CardExtraAttribute,
  CardLanguage,
  conditionConfig,
  ConditionEnum,
  ConditionStrictness,
  ConditionTypesEnum,
  CountryEdition,
  getNodeForArticle,
  InventoryArticle,
  PriceGuideMetrics,
  PriceStategyTarget,
  PricingPlan,
  PricingPlanNode,
  PricingPlanNodeCondition,
} from 'shared'
import { EXTRA_ATTRIBUTES_VALUES_AND_IMAGES_MAP } from 'utils/constants'
import { v4 as uuid } from 'uuid'
import {
  CardExtraInput,
  CardnameInput,
  CommentInput,
  ConditionMakerContext,
  ExpansionInput,
  PriceGuideInput,
  PriceInput,
  QuantityInput,
  RarityInput,
} from '../../PricingPlan/ConditionMakerPresenter'
import { ConditionPresenter } from '../../PricingPlan/ConditionPresenter'
import { PricingPlanContext } from '../../PricingPlan/PricingPlanPresenter'
import { AffectedStockPresenter } from '../../PricingPlan/SortableTree/AffectedStockPresenter'
import { PricingStrategyBox } from '../PricingStrategyBox/PricingStrategyBox'
import './flattened-pricing-plan-presenter.css'

type AccumulatedCondition = {
  condition: PricingPlanNodeCondition
  ownerNodeId: string
}

// All domain types can be created inline. 5 have a custom editor in this
// file (Language, FinishType, CountryEdition, CardCondition, Quantity);
// the other 7 are rendered via a shim (LegacyConditionEditor) that reuses
// the existing inputs from ConditionMakerPresenter.
const SUPPORTED_CREATE_TYPES: ConditionTypesEnum[] = Object.values(ConditionTypesEnum)

const initialValueForType = (type: ConditionTypesEnum): unknown => {
  switch (type) {
    case ConditionTypesEnum.Language:
    case ConditionTypesEnum.FinishType:
    case ConditionTypesEnum.CountryEdition:
    case ConditionTypesEnum.Printing:
    case ConditionTypesEnum.Expansion:
      return []
    case ConditionTypesEnum.CardCondition:
      return { min: ConditionEnum.MT, max: ConditionEnum.PO }
    case ConditionTypesEnum.Quantity:
    case ConditionTypesEnum.Rarity:
      return { min: 1, max: 24 }
    case ConditionTypesEnum.Comment:
      return ''
    case ConditionTypesEnum.PriceGuide:
      return { metric: PriceGuideMetrics.AVG1 }
    case ConditionTypesEnum.Price:
      return { min: undefined, max: undefined }
    case ConditionTypesEnum.CardExtra:
      return Object.values(CardExtraAttribute).reduce(
        (acc, key) => ({ ...acc, [key]: AnyYesNo.Any }),
        {} as Record<CardExtraAttribute, AnyYesNo>
      )
    default:
      return null
  }
}

const LEGACY_INPUT_FOR_TYPE: Partial<Record<ConditionTypesEnum, React.FC>> = {
  [ConditionTypesEnum.Printing]: CardnameInput,
  [ConditionTypesEnum.Expansion]: ExpansionInput,
  [ConditionTypesEnum.PriceGuide]: PriceGuideInput,
  [ConditionTypesEnum.CardExtra]: CardExtraInput,
  [ConditionTypesEnum.Comment]: CommentInput,
  [ConditionTypesEnum.Rarity]: RarityInput,
  [ConditionTypesEnum.Price]: PriceInput,
  [ConditionTypesEnum.Quantity]: QuantityInput,
}

// Reuses the legacy inputs from ConditionMakerPresenter for the types not
// covered by custom editors. Builds a context shim (PricingPlanContext +
// ConditionMakerContext) with state controlled here, and propagates
// changes via onSave with a small debounce (useful for TextField inputs
// like Comment/Price/PriceGuide where every keystroke would trigger a save).
const LegacyConditionEditor = ({
  condition,
  onSave,
}: {
  condition: PricingPlanNodeCondition
  onSave: (newCondition: PricingPlanNodeCondition) => void
}): JSX.Element => {
  const { t } = useTranslation()
  const InputComponent = LEGACY_INPUT_FOR_TYPE[condition.type]
  const [state, setState] = useState<{ type: ConditionTypesEnum; value: unknown }>({
    type: condition.type,
    value: condition.value,
  })
  const [valid, setValid] = useState(false)
  const initialJson = useMemo(
    () => JSON.stringify(condition.value),
    // re-mount via key={c.id} handles the "different condition" case
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [condition.id]
  )

  useEffect(() => {
    if (!valid) return
    if (JSON.stringify(state.value) === initialJson) return
    const timer = setTimeout(() => {
      onSave({ ...condition, value: state.value } as PricingPlanNodeCondition)
    }, 400)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, valid])

  const pricingPlanContextValue = useMemo(
    () =>
      ({
        conditionCtx: condition,
        setConditionCtx: () => undefined,
      } as unknown as React.ContextType<typeof PricingPlanContext>),
    [condition]
  )

  if (!InputComponent) {
    return (
      <p className="flat-plan-editor__label">
        {t('priceUpdates.flatInlineUnavailable', { type: condition.type })}
      </p>
    )
  }

  return (
    <PricingPlanContext.Provider value={pricingPlanContextValue}>
      <ConditionMakerContext.Provider value={{ state, setState, setValid }}>
        <InputComponent />
      </ConditionMakerContext.Provider>
    </PricingPlanContext.Provider>
  )
}

const CHIP_LEAVE_DURATION_MS = 180

const ClickableCondition = ({
  acc,
  isEditing,
  onClick,
  onDelete,
}: {
  acc: AccumulatedCondition
  isEditing?: boolean
  onClick?: (acc: AccumulatedCondition) => void
  onDelete?: (acc: AccumulatedCondition) => void
}): JSX.Element => {
  const [isLeaving, setIsLeaving] = useState(false)
  return (
    <div
      className={`flat-plan-chip${onClick ? ' flat-plan-chip--clickable' : ''}${
        isEditing ? ' flat-plan-chip--editing' : ''
      }${isLeaving ? ' flat-plan-chip--leaving' : ''}`}
      data-condition-id={acc.condition.id}
      onClick={() => !isLeaving && onClick?.(acc)}
    >
      {onDelete && (
        <IconButton
          size="small"
          className="flat-plan-chip__delete"
          disabled={isLeaving}
          onClick={(e) => {
            e.stopPropagation()
            if (isLeaving) return
            setIsLeaving(true)
            setTimeout(() => onDelete(acc), CHIP_LEAVE_DURATION_MS)
          }}
          aria-label="Delete condition"
        >
          <DeleteOutline fontSize="small" />
        </IconButton>
      )}
      <ConditionPresenter condition={acc.condition} />
    </div>
  )
}

const IGNORE_VALUE = '__ignore__'

const StrategySelect = ({
  value,
  onChange,
}: {
  value: string | null | undefined
  onChange?: (newMethod: string | null) => void | Promise<void>
}): JSX.Element => {
  const { user } = useUser()
  const { t } = useTranslation()
  // Pricing plans price the sell side: a buy strategy picked here would be published as a
  // sell price. Excluding buyPrice rather than keeping only sellPrice also covers the
  // strategies seeded in v1 that never got a target backfilled.
  const strategies = (user?.pricingStrategies ?? []).filter(
    (strategy) => strategy.target !== PriceStategyTarget.buyPrice
  )
  const display = value ?? IGNORE_VALUE

  return (
    <FormControl size="small" className="flat-plan-strategy-select">
      <Select
        value={display}
        disabled={!onChange}
        onChange={(e) => {
          if (!onChange) return
          const v = e.target.value as string
          void onChange(v === IGNORE_VALUE ? null : v)
        }}
      >
        <MenuItem value={IGNORE_VALUE}>
          <CustomTooltip title={t('priceUpdates.ignoreExplainer') as string}>
            <span className="flat-plan-strategy-select__option">{t('priceUpdates.ignore')}</span>
          </CustomTooltip>
        </MenuItem>
        {strategies.map((s) => (
          <MenuItem key={s._id} value={s._id}>
            <CustomTooltip
              title={<PricingStrategyBox competitors={user?.competitors} pricingStrategy={s} />}
            >
              <span className="flat-plan-strategy-select__option flat-plan-strategy-select__option--primary">
                <b>{s.name}</b>
              </span>
            </CustomTooltip>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

const RuleCard = ({
  node,
  isFirst,
  isLast,
  affectedStock,
  totalStockCount,
  isActive,
  pendingDraft,
  isPickingType,
  editingConditionId,
  onConditionClick,
  onConditionDelete,
  onMoveUp,
  onMoveDown,
  onMethodChange,
  onAddCondition,
  onStrictnessChange,
}: {
  node: PricingPlanNode
  isFirst: boolean
  isLast: boolean
  affectedStock: InventoryArticle[]
  totalStockCount: number
  isActive: boolean
  pendingDraft: PricingPlanNodeCondition | null
  isPickingType: boolean
  editingConditionId: string | null
  onConditionClick?: (acc: AccumulatedCondition) => void
  onConditionDelete?: (acc: AccumulatedCondition) => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  onMethodChange?: (newMethod: string | null) => void | Promise<void>
  onAddCondition?: () => void
  onStrictnessChange?: (newStrictness: ConditionStrictness) => void | Promise<void>
}): JSX.Element => {
  const { t } = useTranslation()
  const presentTypes = new Set(node.conditions.map((c) => c.type))
  const hasAvailableTypes = SUPPORTED_CREATE_TYPES.some((type) => !presentTypes.has(type))
  const joinerLabel = node.conditionStrictness === ConditionStrictness.all ? 'AND' : 'OR'
  const toggleStrictness = onStrictnessChange
    ? () => {
        const next =
          node.conditionStrictness === ConditionStrictness.all
            ? ConditionStrictness.atleastOne
            : ConditionStrictness.all
        void onStrictnessChange(next)
      }
    : undefined
  const showPlaceholder = isPickingType || Boolean(pendingDraft)
  const showAddButton = Boolean(onAddCondition) && hasAvailableTypes && !showPlaceholder
  return (
    <div
      className={`flat-plan-box${isActive ? ' flat-plan-box--active' : ''}`}
      data-node-id={node.id}
    >
      <div className="flat-plan-box__arrows">
        <IconButton
          size="small"
          disabled={isFirst || !onMoveUp}
          onClick={onMoveUp}
          style={{ visibility: isFirst ? 'hidden' : 'visible' }}
        >
          <ArrowUpward fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          disabled={isLast || !onMoveDown}
          onClick={onMoveDown}
          style={{ visibility: isLast ? 'hidden' : 'visible' }}
        >
          <ArrowDownward fontSize="small" />
        </IconButton>
      </div>
      <div className="flat-plan-row">
        <div className="flat-plan-row__conditions">
          {node.conditions.map((c, i) => (
            <React.Fragment key={c.id}>
              {i > 0 && (
                <button
                  type="button"
                  className="flat-plan-row__joiner"
                  onClick={toggleStrictness}
                  disabled={!toggleStrictness}
                  aria-label={`Switch to ${joinerLabel === 'AND' ? 'OR' : 'AND'}`}
                >
                  {joinerLabel}
                </button>
              )}
              <ClickableCondition
                acc={{ condition: c, ownerNodeId: node.id }}
                isEditing={editingConditionId === c.id}
                onClick={onConditionClick}
                onDelete={onConditionDelete}
              />
            </React.Fragment>
          ))}
          {showPlaceholder && (
            <>
              {node.conditions.length > 0 && (
                <button
                  type="button"
                  className="flat-plan-row__joiner"
                  onClick={toggleStrictness}
                  disabled={!toggleStrictness}
                  aria-label={`Switch to ${joinerLabel === 'AND' ? 'OR' : 'AND'}`}
                >
                  {joinerLabel}
                </button>
              )}
              {pendingDraft ? (
                <div className="flat-plan-chip flat-plan-chip--pending">
                  <ConditionPresenter condition={pendingDraft} />
                </div>
              ) : (
                <div className="flat-plan-chip flat-plan-chip--placeholder">
                  {t('priceUpdates.flatNewConditionPlaceholder')}
                </div>
              )}
            </>
          )}
          {showAddButton && (
            <IconButton
              size="small"
              className="ms-2"
              onClick={onAddCondition}
              aria-label="Add condition"
            >
              <AddCircleOutline />
            </IconButton>
          )}
        </div>
        <div className="flat-plan-row__actions">
          <div className="flat-plan-affected-stock">
            <AffectedStockPresenter
              affectedStock={affectedStock}
              totalStockCount={totalStockCount}
            />
          </div>
          <StrategySelect value={node.method} onChange={onMethodChange} />
        </div>
      </div>
    </div>
  )
}

const DraftBoxView = ({
  draftRule,
  hasAvailableTypes,
  canSubmit,
  pendingDraft,
  isPickingType,
  editingConditionId,
  onConditionClick,
  onConditionDelete,
  onAddCondition,
  onSubmit,
  onDiscard,
  onStrictnessChange,
}: {
  draftRule: PricingPlanNode
  hasAvailableTypes: boolean
  canSubmit: boolean
  pendingDraft: PricingPlanNodeCondition | null
  isPickingType: boolean
  editingConditionId: string | null
  onConditionClick: (acc: AccumulatedCondition) => void
  onConditionDelete: (acc: AccumulatedCondition) => void
  onAddCondition: () => void
  onSubmit: () => void
  onDiscard: () => void
  onStrictnessChange: (newStrictness: ConditionStrictness) => void
}): JSX.Element => {
  const { t } = useTranslation()
  const joinerLabel = draftRule.conditionStrictness === ConditionStrictness.all ? 'AND' : 'OR'
  const toggleStrictness = (): void => {
    onStrictnessChange(
      draftRule.conditionStrictness === ConditionStrictness.all
        ? ConditionStrictness.atleastOne
        : ConditionStrictness.all
    )
  }
  const showPlaceholder = isPickingType || Boolean(pendingDraft)
  const showAddButton = hasAvailableTypes && draftRule.conditions.length > 0 && !showPlaceholder
  return (
    <div className="flat-plan-draft-box">
      <div className="flat-plan-row__conditions">
        {draftRule.conditions.map((c, i) => (
          <React.Fragment key={c.id}>
            {i > 0 && (
              <button
                type="button"
                className="flat-plan-row__joiner"
                onClick={toggleStrictness}
                aria-label={`Switch to ${joinerLabel === 'AND' ? 'OR' : 'AND'}`}
              >
                {joinerLabel}
              </button>
            )}
            <ClickableCondition
              acc={{ condition: c, ownerNodeId: draftRule.id }}
              isEditing={editingConditionId === c.id}
              onClick={onConditionClick}
              onDelete={onConditionDelete}
            />
          </React.Fragment>
        ))}
        {showPlaceholder && (
          <>
            {draftRule.conditions.length > 0 && (
              <button
                type="button"
                className="flat-plan-row__joiner"
                onClick={toggleStrictness}
                aria-label={`Switch to ${joinerLabel === 'AND' ? 'OR' : 'AND'}`}
              >
                {joinerLabel}
              </button>
            )}
            {pendingDraft ? (
              <div className="flat-plan-chip flat-plan-chip--pending">
                <ConditionPresenter condition={pendingDraft} />
              </div>
            ) : (
              <div className="flat-plan-chip flat-plan-chip--placeholder">
                {t('priceUpdates.flatNewConditionPlaceholder')}
              </div>
            )}
          </>
        )}
        {showAddButton && (
          <IconButton
            size="small"
            className="ms-2"
            onClick={onAddCondition}
            aria-label="Add condition"
          >
            <AddCircleOutline />
          </IconButton>
        )}
      </div>
      <div className="flat-plan-draft-box__actions">
        <Button variant="outlined" onClick={onDiscard} className="me-2">
          {t('common.cancel')}
        </Button>
        <Button variant="contained" color="primary" disabled={!canSubmit} onClick={onSubmit}>
          {t('priceUpdates.flatSaveRule')}
        </Button>
      </div>
    </div>
  )
}

const DefaultBox = ({
  method,
  affectedStock,
  totalStockCount,
  onMethodChange,
}: {
  method: string | null
  affectedStock: InventoryArticle[]
  totalStockCount: number
  onMethodChange?: (newMethod: string | null) => void | Promise<void>
}): JSX.Element => (
  <div className="flat-plan-default-box">
    <div className="flat-plan-default-box__label-wrap">
      <p className="flat-plan-default-box__label">DEFAULT FOR ALL ARTICLES</p>
    </div>
    <div className="flat-plan-affected-stock">
      <AffectedStockPresenter affectedStock={affectedStock} totalStockCount={totalStockCount} />
    </div>
    <StrategySelect value={method} onChange={onMethodChange} />
  </div>
)

const LANGUAGE_LIST: { id: CardLanguage; label: string }[] = Object.keys(CardLanguage)
  .filter((k) => /^\d+$/.test(k))
  .map((k) => ({ id: Number(k) as CardLanguage, label: CardLanguage[Number(k)] }))

const CONDITION_LIST: { id: ConditionEnum; label: string }[] = [
  { id: ConditionEnum.MT, label: 'MT' },
  { id: ConditionEnum.NM, label: 'NM' },
  { id: ConditionEnum.EX, label: 'EX' },
  { id: ConditionEnum.GD, label: 'GD' },
  { id: ConditionEnum.LP, label: 'LP' },
  { id: ConditionEnum.PL, label: 'PL' },
  { id: ConditionEnum.PO, label: 'PO' },
]

const LanguageEditor = ({
  condition,
  onSave,
}: {
  condition: PricingPlanNodeCondition
  onSave: (c: PricingPlanNodeCondition) => void
}): JSX.Element => {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<CardLanguage[]>(
    (condition.value as CardLanguage[]) ?? []
  )
  return (
    <MultipleSelect<CardLanguage>
      label={t('priceUpdates.ruleTypes.Language')}
      value={selected}
      options={LANGUAGE_LIST.map((l) => ({ value: l.id, label: l.label }))}
      onChange={(next) => {
        setSelected(next)
        if (next.length > 0) {
          onSave({ ...condition, value: next } as PricingPlanNodeCondition)
        }
      }}
    />
  )
}

const CardConditionEditor = ({
  condition,
  onSave,
}: {
  condition: PricingPlanNodeCondition
  onSave: (c: PricingPlanNodeCondition) => void
}): JSX.Element => {
  const { t } = useTranslation()
  const v = condition.value as { min: number; max: number }
  const [range, setRange] = useState<[number, number]>([
    v.min ?? ConditionEnum.MT,
    v.max ?? ConditionEnum.PO,
  ])
  return (
    <div>
      <p className="flat-plan-editor__label">{t('priceUpdates.flatConditionRange')}</p>
      <div className="flat-plan-editor__slider">
        <Slider
          value={range}
          onChange={(_, newValue) => setRange(newValue as [number, number])}
          onChangeCommitted={(_, newValue) => {
            const [min, max] = newValue as [number, number]
            onSave({
              ...condition,
              value: { min, max },
            } as PricingPlanNodeCondition)
          }}
          valueLabelDisplay="auto"
          valueLabelFormat={(val) => CONDITION_LIST[val]?.label ?? String(val)}
          step={1}
          marks={CONDITION_LIST.map((c) => ({ value: c.id, label: c.label }))}
          min={ConditionEnum.MT}
          max={ConditionEnum.PO}
        />
      </div>
    </div>
  )
}

const FinishTypeEditor = ({
  condition,
  onSave,
}: {
  condition: PricingPlanNodeCondition
  onSave: (c: PricingPlanNodeCondition) => void
}): JSX.Element => {
  const { t } = useTranslation()
  const { activeGame } = useUser()
  const finishTypes = Array.isArray(activeGame?.finishType) ? activeGame.finishType : []
  const [selected, setSelected] = useState<string[]>(
    Array.isArray(condition.value) ? (condition.value as string[]) : []
  )
  if (finishTypes.length === 0) {
    return <p className="flat-plan-editor__label">{t('priceUpdates.flatFinishTypeEmpty')}</p>
  }
  return (
    <MultipleSelect<string>
      label={t('priceUpdates.ruleTypes.FinishType')}
      value={selected}
      options={finishTypes.map((type) => ({ value: type, label: type }))}
      onChange={(next) => {
        setSelected(next)
        if (next.length > 0) {
          onSave({ ...condition, value: next } as PricingPlanNodeCondition)
        }
      }}
    />
  )
}

const CountryEditionEditor = ({
  condition,
  onSave,
}: {
  condition: PricingPlanNodeCondition
  onSave: (c: PricingPlanNodeCondition) => void
}): JSX.Element => {
  const { t } = useTranslation()
  const { activeGame } = useUser()
  const gameInformations = EXTRA_ATTRIBUTES_VALUES_AND_IMAGES_MAP.find(
    (game) => game.idGame === activeGame?.idGame
  )
  const countryEditionOptions = gameInformations?.countryEdition?.length
    ? gameInformations.countryEdition
    : [CountryEdition.Regular]
  const [selected, setSelected] = useState<CountryEdition[]>(
    Array.isArray(condition.value) ? (condition.value as CountryEdition[]) : []
  )
  return (
    <MultipleSelect<CountryEdition>
      label={t('priceUpdates.ruleTypes.CountryEdition')}
      value={selected}
      options={countryEditionOptions.map((type) => ({
        value: type,
        label:
          type !== CountryEdition.Regular ? (
            <Flag
              id={`${type}-flag`}
              // @ts-ignore
              fallback={<span>{type}</span>}
              height="15"
              code={type.toLocaleLowerCase()}
            />
          ) : (
            <span>{t('priceUpdates.flatCountryEditionRegular')}</span>
          ),
      }))}
      onChange={(next) => {
        setSelected(next)
        if (next.length > 0) {
          onSave({ ...condition, value: next } as PricingPlanNodeCondition)
        }
      }}
    />
  )
}

type CreatingState = {
  ownerNodeId: string
  draft: PricingPlanNodeCondition | null
  presentTypes: Set<ConditionTypesEnum>
}

const RuleDesignerPopover = ({
  anchorPosition,
  acc,
  creating,
  onClose,
  onSave,
  onPickType,
}: {
  anchorPosition: { top: number; left: number } | null
  acc: AccumulatedCondition | null
  creating: CreatingState | null
  onClose: () => void
  onSave: (newCondition: PricingPlanNodeCondition) => void | Promise<void>
  onPickType: (type: ConditionTypesEnum) => void
}): JSX.Element => {
  const { t } = useTranslation()
  const inTypePicker = Boolean(creating && !creating.draft)
  const c = creating?.draft ?? acc?.condition ?? null
  const open = anchorPosition != null && (Boolean(creating) || Boolean(acc))

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition ?? undefined}
      // Ignore backdrop clicks: the popover closes ONLY via the explicit
      // Cancel / Save condition buttons.
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return
        onClose()
      }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      classes={{ paper: 'flat-plan-popover__paper' }}
    >
      <div className="flat-plan-popover">
        {c && !inTypePicker && <p className="flat-plan-popover__condition-type">{c.type}</p>}
        <p className="flat-plan-popover__intro">
          {inTypePicker ? t('priceUpdates.flatPickerIntro') : t('priceUpdates.flatEditIntro')}
        </p>

        {inTypePicker && creating ? (
          <>
            <div className="mt-3">
              <FormControl size="small" fullWidth>
                <Select
                  value=""
                  displayEmpty
                  onChange={(e) => {
                    // The header inside the menu is not an option: clicking it sends an undefined value
                    const v = e.target.value as ConditionTypesEnum | '' | undefined
                    if (v) onPickType(v)
                  }}
                  renderValue={() => (
                    <span className="flat-plan-popover__picker-placeholder">
                      {t('priceUpdates.flatPickerPlaceholder')}
                    </span>
                  )}
                >
                  <div className="px-3">
                    <p className="flat-plan-popover__picker-header-text">
                      {t('priceUpdates.createRuleBasedOn')}
                    </p>
                    <Divider />
                  </div>
                  {Object.keys(ConditionTypesEnum).map((key) => {
                    const typeValue = ConditionTypesEnum[key as keyof typeof ConditionTypesEnum]
                    const alreadyPresent = creating.presentTypes.has(typeValue)
                    const inlineSupported = SUPPORTED_CREATE_TYPES.includes(typeValue)
                    const disabled = alreadyPresent || !inlineSupported
                    return (
                      <MenuItem key={key} value={typeValue} disabled={disabled}>
                        <div>
                          <span>{t(`priceUpdates.ruleTypes.${key}`)}</span>
                          <FormHelperText>
                            {t(`priceUpdates.ruleTypes.${key}Explainer`)}
                          </FormHelperText>
                        </div>
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>
            </div>
            <div className="mt-4 text-center">
              <Button variant="outlined" onClick={onClose}>
                {t('common.cancel')}
              </Button>
            </div>
          </>
        ) : c ? (
          <>
            <div className="mt-3">
              {c.type === ConditionTypesEnum.Language && (
                <LanguageEditor key={c.id} condition={c} onSave={onSave} />
              )}
              {c.type === ConditionTypesEnum.CardCondition && (
                <CardConditionEditor key={c.id} condition={c} onSave={onSave} />
              )}
              {c.type === ConditionTypesEnum.FinishType && (
                <FinishTypeEditor key={c.id} condition={c} onSave={onSave} />
              )}
              {c.type === ConditionTypesEnum.CountryEdition && (
                <CountryEditionEditor key={c.id} condition={c} onSave={onSave} />
              )}
              {c.type !== ConditionTypesEnum.Language &&
                c.type !== ConditionTypesEnum.CardCondition &&
                c.type !== ConditionTypesEnum.FinishType &&
                c.type !== ConditionTypesEnum.CountryEdition && (
                  <LegacyConditionEditor key={c.id} condition={c} onSave={onSave} />
                )}
            </div>

            {conditionConfig[c.type]?.negatable && (
              <div className="flat-plan-popover__negate mt-2">
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={Boolean(c.negated)}
                      onChange={() => onSave({ ...c, negated: !c.negated })}
                    />
                  }
                  label={t('common.negate')}
                />
              </div>
            )}

            <div className="mt-4 text-center flat-plan-popover__actions">
              <Button variant="outlined" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  // Conditions are saved on change, so an untouched new quantity would be lost:
                  // its default 1-24 is already a real rule (it excludes more than 24)
                  if (creating?.draft?.type === ConditionTypesEnum.Quantity) {
                    await onSave(creating.draft)
                  }
                  onClose()
                }}
              >
                {t('priceUpdates.flatSaveCondition')}
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </Popover>
  )
}

export const FlattenedPricingPlanPresenter = React.memo(function FlattenedPricingPlanPresenter({
  defaultMethod,
  nodes,
  onConditionEdit,
  onConditionDelete,
  onConditionAdd,
  onReorderNodes,
  onMethodChange,
  onCreateRule,
  onStrictnessChange,
}: {
  defaultMethod: string | null
  nodes: PricingPlanNode[]
  onConditionEdit?: (
    ownerNodeId: string,
    conditionId: string,
    newCondition: PricingPlanNodeCondition
  ) => void | Promise<void>
  onConditionDelete?: (ownerNodeId: string, conditionId: string) => void | Promise<void>
  onConditionAdd?: (
    ownerNodeId: string,
    newCondition: PricingPlanNodeCondition
  ) => void | Promise<void>
  onReorderNodes?: (newOrderedNodeIds: string[]) => void | Promise<void>
  onMethodChange?: (nodeId: string, newMethod: string | null) => void | Promise<void>
  onCreateRule?: (newNode: PricingPlanNode, insertIndex: number) => void | Promise<void>
  onStrictnessChange?: (
    ownerNodeId: string,
    newStrictness: ConditionStrictness
  ) => void | Promise<void>
}): JSX.Element {
  const [editing, setEditing] = useState<AccumulatedCondition | null>(null)
  const [creating, setCreating] = useState<CreatingState | null>(null)
  const [draftRule, setDraftRule] = useState<PricingPlanNode | null>(null)
  // Popover position captured ONCE per anchor target change (different
  // chip). We use anchorReference="anchorPosition" instead of anchorEl
  // because MUI Popover re-positions on every render based on
  // anchorEl.getBoundingClientRect(). The editor chip grows in
  // width/height every time the value changes (e.g. flags added), so the
  // rect changes → popover shifts → inner MUI Select dropdown opens in
  // different positions → micro-jitter.
  // anchorPosition is a fixed coordinate: we capture it when anchoring to
  // a new chip and it stays put until the chip changes.
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null)
  const lastAnchorKeyRef = useRef<string | null>(null)
  // Transient flag to apply --entering to the draft box only during the
  // animation (320ms). Without this, the class would always be present →
  // the anchor effect would always find `target.closest('--entering')` →
  // 340ms delay on every interaction → stale popover at (0,0) during the
  // delay because anchorEl points to a chip that's already unmounted.
  //
  // CRUCIAL: setDraftEntering happens during render (setState-during-
  // render guarded by ref), not inside an effect. If we waited for
  // useEffect, the FIRST render of the wrapper would have no --entering →
  // the anchor effect would fire without seeing --entering →
  // setAnchorEl(placeholder) immediately → popover opens on a chip still
  // in mid-translateY → MUI re-positions on every render while the chip
  // translates toward 0 → popover visibly shifts.
  const [draftEnteringId, setDraftEnteringId] = useState<string | null>(null)
  const lastDraftIdRef = useRef<string | null>(null)
  if (draftRule && draftRule.id !== lastDraftIdRef.current) {
    lastDraftIdRef.current = draftRule.id
    setDraftEnteringId(draftRule.id)
  }
  if (!draftRule && lastDraftIdRef.current !== null) {
    lastDraftIdRef.current = null
  }
  useEffect(() => {
    if (!draftEnteringId) return
    const t = setTimeout(() => {
      setDraftEnteringId((prev) => (prev === draftEnteringId ? null : prev))
    }, 320)
    return () => clearTimeout(t)
  }, [draftEnteringId])
  const draftEntering = draftRule != null && draftEnteringId === draftRule.id

  // Entry + exit animations driven by CSS (interpolate-size: allow-keywords
  // makes the height: auto → 0 transition work without JS measuring). React
  // only tracks which ids are in "entering" or "leaving" state; the actual
  // animations live in the CSS.
  const ROW_ENTER_DURATION_MS = 320
  const ROW_LEAVE_DURATION_MS = 320
  const prevNodesRef = useRef<PricingPlanNode[]>(nodes)
  const [enteringIds, setEnteringIds] = useState<Set<string>>(() => new Set())
  const [leavingNodes, setLeavingNodes] = useState<{ node: PricingPlanNode; insertIdx: number }[]>(
    []
  )
  const cardsContainerRef = useRef<HTMLDivElement>(null)

  // displayNodes includes both the current nodes and the ones in the
  // leave phase (detected synchronously via prevNodesRef to keep the
  // fiber stable → the className goes from "" → "--leaving" on the same
  // DOM element → CSS transition fires).
  const displayNodes = useMemo<(PricingPlanNode & { _leaving?: boolean })[]>(() => {
    const currentIds = new Set(nodes.map((n) => n.id))
    const knownLeavingIds = new Set(leavingNodes.map((e) => e.node.id))
    const justRemovedThisRender = prevNodesRef.current
      .filter((n) => !currentIds.has(n.id) && !knownLeavingIds.has(n.id))
      .map((n) => ({
        node: n,
        insertIdx: prevNodesRef.current.findIndex((x) => x.id === n.id),
      }))
    const allLeaving = [...leavingNodes, ...justRemovedThisRender].sort(
      (a, b) => a.insertIdx - b.insertIdx
    )
    if (allLeaving.length === 0) return nodes
    const list: (PricingPlanNode & { _leaving?: boolean })[] = [...nodes]
    for (const entry of allLeaving) {
      const idx = Math.min(entry.insertIdx, list.length)
      list.splice(idx, 0, { ...entry.node, _leaving: true })
    }
    return list
  }, [nodes, leavingNodes])

  useLayoutEffect(() => {
    const currentIds = new Set(nodes.map((n) => n.id))
    const prevIds = new Set(prevNodesRef.current.map((n) => n.id))
    const knownLeavingIds = new Set(leavingNodes.map((e) => e.node.id))
    // Leave detection.
    const justRemoved = prevNodesRef.current.filter(
      (n) => !currentIds.has(n.id) && !knownLeavingIds.has(n.id)
    )
    if (justRemoved.length > 0) {
      const toAdd = justRemoved.map((n) => ({
        node: n,
        insertIdx: prevNodesRef.current.findIndex((x) => x.id === n.id),
      }))
      setLeavingNodes((prev) => [...prev, ...toAdd])
      toAdd.forEach((entry) => {
        setTimeout(() => {
          setLeavingNodes((prev) => prev.filter((p) => p.node.id !== entry.node.id))
        }, ROW_LEAVE_DURATION_MS)
      })
    }
    // Entry detection.
    const justAdded = nodes.filter((n) => !prevIds.has(n.id))
    if (justAdded.length > 0) {
      const newIds = justAdded.map((n) => n.id)
      setEnteringIds((prev) => {
        const next = new Set(prev)
        newIds.forEach((id) => next.add(id))
        return next
      })
      newIds.forEach((id) => {
        setTimeout(() => {
          setEnteringIds((prev) => {
            const next = new Set(prev)
            next.delete(id)
            return next
          })
        }, ROW_ENTER_DURATION_MS)
      })
    }
    prevNodesRef.current = nodes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes])

  // FLIP animation for card reordering via up/down arrows.
  // We only animate nodes whose index shift is NOT explained by the
  // removal of earlier nodes (= a real reorder). Otherwise we'd see a
  // "jump back + animate forward" on siblings below a leaving row.
  const prevRectsRef = useRef<Map<string, DOMRect>>(new Map())
  const prevOrderRef = useRef<string[]>([])
  useLayoutEffect(() => {
    const container = cardsContainerRef.current
    if (!container) return
    const newOrder = displayNodes.map((n) => n.id)
    const oldOrder = prevOrderRef.current
    const newOrderSet = new Set(newOrder)
    const reordered = new Set<string>()
    newOrder.forEach((id, idx) => {
      const oldIdx = oldOrder.indexOf(id)
      if (oldIdx === -1 || oldIdx === idx) return
      // Compute the "expected" index assuming the only change is the
      // removal of ids that were in oldOrder but no longer in newOrder.
      // If it matches the current index, it's a shift caused by removals
      // → don't FLIP.
      let removedBefore = 0
      for (let i = 0; i < oldIdx; i++) {
        if (!newOrderSet.has(oldOrder[i])) removedBefore++
      }
      const expectedIdx = oldIdx - removedBefore
      if (expectedIdx !== idx) reordered.add(id)
    })
    const cards = container.querySelectorAll<HTMLElement>('[data-node-id]')
    const newRects = new Map<string, DOMRect>()
    cards.forEach((el) => {
      const id = el.dataset.nodeId
      if (id) newRects.set(id, el.getBoundingClientRect())
    })
    cards.forEach((el) => {
      const id = el.dataset.nodeId
      if (!id || !reordered.has(id)) return
      // Apply the transform to the OUTER wrapper (.flat-plan-box-outer),
      // not the inner box: the wrapper has overflow: hidden for the leave
      // animation, which would clip a transform on the inner box during
      // the reorder.
      const wrapper = el.parentElement as HTMLElement | null
      if (!wrapper) return
      const newRect = newRects.get(id)
      const oldRect = prevRectsRef.current.get(id)
      if (newRect && oldRect && Math.abs(oldRect.top - newRect.top) > 1) {
        const dy = oldRect.top - newRect.top
        wrapper.style.transform = `translateY(${dy}px)`
        wrapper.style.transition = 'none'
        requestAnimationFrame(() => {
          wrapper.style.transform = ''
          wrapper.style.transition = 'transform 250ms ease'
        })
      }
    })
    prevRectsRef.current = newRects
    prevOrderRef.current = newOrder
  }, [displayNodes])

  const { getPriceGuide } = usePriceGuide()
  const [fetchedStock] = usePersistentStock({
    stock: [],
    sealedStock: [],
    fetchedAt: '',
  })

  const planLike = useMemo(
    () =>
      ({
        defaultNode: { id: 'default', method: defaultMethod },
        nodes,
      } as unknown as PricingPlan),
    [defaultMethod, nodes]
  )

  const stockByNode = useMemo(() => {
    const byNode: Record<string, InventoryArticle[]> = {}
    fetchedStock.stock.forEach((article) => {
      if (!article.graded?.isGraded) {
        const { id } = getNodeForArticle(planLike, article, getPriceGuide(article.idProduct))
        if (!byNode[id]) byNode[id] = []
        byNode[id].push(article)
      }
    })
    return byNode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planLike, fetchedStock.stock.length])

  const totalStockCount = fetchedStock.stock.length

  // Auto-save on every change: updates the plan via API but keeps the
  // sidebar open on the same condition so the user can keep editing
  // uninterrupted.
  // For the draftRule (new rule being composed) changes stay local until
  // the user clicks "Save rule" on the ghost box.
  const handleSave = async (newCondition: PricingPlanNodeCondition): Promise<void> => {
    // Draft branch: ADD a new condition to the draftRule
    if (creating && creating.draft && draftRule && creating.ownerNodeId === draftRule.id) {
      const newConds = [...draftRule.conditions, newCondition]
      setDraftRule({
        ...draftRule,
        conditions: newConds,
      })
      setCreating(null)
      setEditing({ condition: newCondition, ownerNodeId: draftRule.id })
      return
    }
    // Draft branch: EDIT an existing condition in the draftRule
    if (editing && draftRule && editing.ownerNodeId === draftRule.id) {
      setDraftRule({
        ...draftRule,
        conditions: draftRule.conditions.map((c) =>
          c.id === editing.condition.id ? newCondition : c
        ),
      })
      // Keep `editing` in sync so the open popover reflects the change (e.g.
      // the Negate switch), instead of reading the pre-edit snapshot.
      setEditing({ condition: newCondition, ownerNodeId: editing.ownerNodeId })
      return
    }
    // Persisted branch: ADD on a saved node
    if (creating && creating.draft) {
      if (!onConditionAdd) return
      await onConditionAdd(creating.ownerNodeId, newCondition)
      setCreating(null)
      setEditing({ condition: newCondition, ownerNodeId: creating.ownerNodeId })
      return
    }
    // Persisted branch: EDIT on a saved condition
    if (!editing || !onConditionEdit) return
    await onConditionEdit(editing.ownerNodeId, editing.condition.id, newCondition)
    // Keep `editing` in sync so the open popover reflects the change (e.g.
    // the Negate switch), instead of reading the pre-edit snapshot.
    setEditing({ condition: newCondition, ownerNodeId: editing.ownerNodeId })
  }

  const handlePickType = (type: ConditionTypesEnum): void => {
    if (!creating) return
    const draft = {
      id: uuid(),
      type,
      value: initialValueForType(type),
    } as PricingPlanNodeCondition
    setCreating({ ...creating, draft })
  }

  const handleClose = (): void => {
    // If the user is abandoning the type picker on a still-empty draft,
    // discard the draft too — don't leave a ghost box with no conditions.
    // Reads the latest draft: Save condition may have just added one to it
    if (creating) {
      setDraftRule((draft) =>
        draft && creating.ownerNodeId === draft.id && draft.conditions.length === 0 ? null : draft
      )
    }
    setEditing(null)
    setCreating(null)
  }

  // Capture the Popover position when the anchor target changes
  // (different chip). As long as we stay on the same chip, anchorPosition
  // is not updated — the popover stays still even if the chip grows
  // during editing (e.g. user selects more flags → chip widens).
  //
  // Key: identifies the current anchor target. It changes only when we
  // move to a different chip or enter/exit the creating/editing state.
  // We re-capture only when key changes.
  //
  // If the chip is inside a row currently in --entering (320ms animation
  // with translateY 40→0), we delay the capture: getBoundingClientRect on
  // a chip still translating would return coordinates "in transit".
  useLayoutEffect(() => {
    const container = cardsContainerRef.current
    if (!container) {
      setAnchorPosition(null)
      lastAnchorKeyRef.current = null
      return
    }
    let key: string | null = null
    let target: HTMLElement | null = null
    if (creating?.draft) {
      key = `pending:${creating.draft.id}`
      target = container.querySelector('.flat-plan-chip--pending')
    } else if (creating && !creating.draft) {
      key = `picker:${creating.ownerNodeId}`
      target = container.querySelector('.flat-plan-chip--placeholder')
    } else if (editing) {
      key = `editing:${editing.condition.id}`
      target = container.querySelector(`[data-condition-id="${editing.condition.id}"]`)
    }
    if (!target || !key) {
      setAnchorPosition(null)
      lastAnchorKeyRef.current = null
      return
    }
    if (key === lastAnchorKeyRef.current) {
      // Same anchor, don't re-capture (popover stays still).
      return
    }
    const capture = (): void => {
      if (!target) return
      const rect = target.getBoundingClientRect()
      lastAnchorKeyRef.current = key
      setAnchorPosition({ top: rect.bottom, left: rect.left })
    }
    if (target.closest('.flat-plan-box-outer--entering')) {
      const handle = setTimeout(capture, 340)
      return () => clearTimeout(handle)
    }
    capture()
  }, [editing, creating, draftRule, nodes])

  return (
    <div className="flat-plan-root">
      <RuleDesignerPopover
        anchorPosition={anchorPosition}
        acc={editing}
        creating={creating}
        onClose={handleClose}
        onSave={handleSave}
        onPickType={handlePickType}
      />
      <div className="flat-plan-content p-3" ref={cardsContainerRef}>
        {displayNodes.map((n) => {
          const realIndex = nodes.findIndex((nn) => nn.id === n.id)
          const moveNode = (direction: 'up' | 'down'): void => {
            if (!onReorderNodes || realIndex < 0) return
            const target = direction === 'up' ? realIndex - 1 : realIndex + 1
            if (target < 0 || target >= nodes.length) return
            const newIds = nodes.map((nn) => nn.id)
            ;[newIds[realIndex], newIds[target]] = [newIds[target], newIds[realIndex]]
            void onReorderNodes(newIds)
          }
          const activeNodeId = creating?.ownerNodeId ?? editing?.ownerNodeId ?? null
          const creatingForThisNode = creating?.ownerNodeId === n.id ? creating : null
          // Synchronous mount detection: if the node wasn't in
          // `prevNodesRef` (= first render in which it appears), apply
          // --entering right away so the animation starts at mount as it
          // does for the draft box. enteringIds keeps the class applied
          // during the 320ms timeout.
          const isFreshMount = !prevNodesRef.current.some((p) => p.id === n.id)
          const isEntering = !n._leaving && (enteringIds.has(n.id) || isFreshMount)
          return (
            <div
              key={n.id}
              className={`flat-plan-box-outer${
                n._leaving
                  ? ' flat-plan-box-outer--leaving'
                  : isEntering
                  ? ' flat-plan-box-outer--entering'
                  : ''
              }`}
            >
              <RuleCard
                node={n}
                isFirst={realIndex === 0}
                isLast={realIndex === nodes.length - 1}
                affectedStock={stockByNode[n.id] ?? []}
                totalStockCount={totalStockCount}
                isActive={n.id === activeNodeId}
                pendingDraft={creatingForThisNode?.draft ?? null}
                isPickingType={Boolean(creatingForThisNode && !creatingForThisNode.draft)}
                editingConditionId={
                  editing && editing.ownerNodeId === n.id ? editing.condition.id : null
                }
                onConditionClick={
                  onConditionEdit
                    ? (acc) => {
                        setCreating(null)
                        setEditing(acc)
                      }
                    : undefined
                }
                onConditionDelete={
                  onConditionDelete
                    ? (acc) => {
                        void onConditionDelete(acc.ownerNodeId, acc.condition.id)
                        if (editing && editing.condition.id === acc.condition.id) setEditing(null)
                      }
                    : undefined
                }
                onMoveUp={onReorderNodes ? () => moveNode('up') : undefined}
                onMoveDown={onReorderNodes ? () => moveNode('down') : undefined}
                onMethodChange={
                  onMethodChange ? (newMethod) => onMethodChange(n.id, newMethod) : undefined
                }
                onAddCondition={
                  onConditionAdd
                    ? () => {
                        const presentTypes = new Set(n.conditions.map((c) => c.type))
                        const hasEligible = SUPPORTED_CREATE_TYPES.some((t) => !presentTypes.has(t))
                        if (!hasEligible) return
                        setEditing(null)
                        setCreating({ ownerNodeId: n.id, draft: null, presentTypes })
                      }
                    : undefined
                }
                onStrictnessChange={
                  onStrictnessChange
                    ? (newStrictness) => onStrictnessChange(n.id, newStrictness)
                    : undefined
                }
              />
            </div>
          )
        })}
        {draftRule && (
          <div
            className={`flat-plan-box-outer${
              draftEntering ? ' flat-plan-box-outer--entering' : ''
            }`}
          >
            <DraftBoxView
              draftRule={draftRule}
              hasAvailableTypes={SUPPORTED_CREATE_TYPES.some(
                (t) => !new Set(draftRule.conditions.map((c) => c.type)).has(t)
              )}
              canSubmit={draftRule.conditions.length > 0 && Boolean(onCreateRule)}
              pendingDraft={
                creating && creating.ownerNodeId === draftRule.id ? creating.draft : null
              }
              isPickingType={Boolean(
                creating && creating.ownerNodeId === draftRule.id && !creating.draft
              )}
              editingConditionId={
                editing && editing.ownerNodeId === draftRule.id ? editing.condition.id : null
              }
              onConditionClick={(acc) => {
                setCreating(null)
                setEditing(acc)
              }}
              onConditionDelete={(acc) => {
                const newConds = draftRule.conditions.filter((c) => c.id !== acc.condition.id)
                setDraftRule({
                  ...draftRule,
                  conditions: newConds,
                })
                if (editing && editing.condition.id === acc.condition.id) setEditing(null)
                // If all the draft's conditions have been deleted,
                // re-open the type picker so the user can start over
                // without having to click Cancel + New rule from scratch.
                if (newConds.length === 0) {
                  setCreating({
                    ownerNodeId: draftRule.id,
                    draft: null,
                    presentTypes: new Set(),
                  })
                }
              }}
              onAddCondition={() => {
                const presentTypes = new Set(draftRule.conditions.map((c) => c.type))
                const hasEligible = SUPPORTED_CREATE_TYPES.some((t) => !presentTypes.has(t))
                if (!hasEligible) return
                setEditing(null)
                setCreating({ ownerNodeId: draftRule.id, draft: null, presentTypes })
              }}
              onSubmit={async () => {
                if (!onCreateRule || draftRule.conditions.length === 0) return
                await onCreateRule(draftRule, nodes.length)
                setDraftRule(null)
                setEditing(null)
                setCreating(null)
              }}
              onDiscard={() => {
                setDraftRule(null)
                setEditing(null)
                setCreating(null)
              }}
              onStrictnessChange={(newStrictness) => {
                setDraftRule({ ...draftRule, conditionStrictness: newStrictness })
              }}
            />
          </div>
        )}
        {/* DefaultBox is the fallback row for all articles not matched by
            any other rule: placed at the bottom, right above the "New rule"
            button. */}
        <DefaultBox
          method={defaultMethod}
          affectedStock={stockByNode['default'] ?? []}
          totalStockCount={totalStockCount}
          onMethodChange={onMethodChange ? (m) => onMethodChange('default', m) : undefined}
        />
        {onCreateRule && !draftRule && (
          <div className="mt-3 text-center">
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCircleOutline />}
              onClick={() => {
                const newDraftId = uuid()
                setEditing(null)
                setDraftRule({
                  id: newDraftId,
                  conditions: [],
                  conditionStrictness: ConditionStrictness.all,
                  method: null,
                  children: [],
                })
                // Open the type picker immediately: the first "click +" is skipped.
                setCreating({
                  ownerNodeId: newDraftId,
                  draft: null,
                  presentTypes: new Set(),
                })
              }}
            >
              New rule
            </Button>
          </div>
        )}
      </div>
    </div>
  )
})
